import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import { navLinks, brand } from '../data/mock';

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#0a1b35]/92 backdrop-blur-xl border-b border-[#e8b863]/15 py-3'
          : 'bg-[#0d2143]/70 backdrop-blur-sm py-4 lg:py-5'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-10 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center group flex-shrink-0">
          <img
            src={brand.logo}
            alt={brand.fullName}
            className="h-11 sm:h-12 lg:h-14 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8 xl:gap-9">
          {navLinks.map((link) => (
            <NavLink
              key={link.label}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) =>
                `relative text-[14px] font-medium tracking-wide transition-colors group ${
                  isActive ? 'text-[#e8b863]' : 'text-white/85 hover:text-[#e8b863]'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {link.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-[#e8b863] transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* CTA */}
        <Link
          to="/contact"
          className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#d49d3f] to-[#b8842a] hover:from-[#e8b863] hover:to-[#d49d3f] text-[#0a1b35] font-semibold text-[13px] tracking-wide transition-all duration-300 shadow-lg shadow-[#d49d3f]/25 hover:shadow-[#e8b863]/35 hover:-translate-y-0.5"
        >
          Book Consultation
          <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
        </Link>

        {/* Mobile toggle */}
        <button
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg text-white hover:bg-white/10 transition-colors"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-[#0a1b35]/97 backdrop-blur-xl border-t border-white/10">
          <nav className="flex flex-col px-5 sm:px-6 py-5 gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.label}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `text-[15px] font-medium py-3 border-b border-white/8 ${
                    isActive ? 'text-[#e8b863]' : 'text-white hover:text-[#e8b863]'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#d49d3f] to-[#b8842a] text-[#0a1b35] font-semibold text-[14px]"
            >
              Book Consultation
              <ArrowRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
